// 💬 Ask Prof — public message box (Welcome page) + admin inbox.
// Firestore "messages": anyone can CREATE (validated in firestore.rules) and
// GET a single message by its id (the private "reply code"); only admin can list / reply / delete.
import React, { useEffect, useState } from "react";
import { db } from "./firebase";
import {
  collection, addDoc, getDoc, getDocs, updateDoc, deleteDoc, doc,
  query, orderBy, serverTimestamp,
} from "firebase/firestore";

const LS_KEY = "askprof_codes";
const loadCodes = () => { try { return JSON.parse(localStorage.getItem(LS_KEY) || "[]"); } catch { return []; } };
const saveCodes = (c) => { try { localStorage.setItem(LS_KEY, JSON.stringify(c.slice(0, 10))); } catch {} };
const when = (t) => t?.seconds ? new Date(t.seconds * 1000).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "";

/* ───────────── Public widget (Welcome page) ───────────── */
export function AskProfWidget() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("send");
  const [f, setF] = useState({ name: "", idNo: "", contact: "", section: "", text: "", website: "" });
  const [sending, setSending] = useState(false);
  const [sentCode, setSentCode] = useState("");
  const [code, setCode] = useState("");
  const [found, setFound] = useState(null);
  const [checking, setChecking] = useState(false);
  const [mine, setMine] = useState([]);

  useEffect(() => {
    if (!open || tab !== "check") return;
    const codes = loadCodes();
    Promise.all(codes.map(c => getDoc(doc(db, "messages", c)).then(s => s.exists() ? { id: s.id, ...s.data() } : null).catch(() => null)))
      .then(list => setMine(list.filter(Boolean)));
  }, [open, tab]);

  const send = async () => {
    if (f.website) return;                                   // honeypot: bots fill hidden field
    const name = f.name.trim(), idNo = f.idNo.trim(), contact = f.contact.trim(), text = f.text.trim();
    if (!name || !idNo || !contact || !text) { alert("Please fill Name, ID, Email/Phone and your message."); return; }
    const last = Number(sessionStorage.getItem("askprof_last") || 0);
    if (Date.now() - last < 60000) { alert("Please wait a minute before sending another message."); return; }
    setSending(true);
    try {
      const ref = await addDoc(collection(db, "messages"), {
        name: name.slice(0, 80), idNo: idNo.slice(0, 40), contact: contact.slice(0, 100),
        section: f.section.trim().slice(0, 60), text: text.slice(0, 1500),
        status: "new", reply: null, createdAt: serverTimestamp(),
      });
      try { sessionStorage.setItem("askprof_last", String(Date.now())); } catch {}
      saveCodes([ref.id, ...loadCodes()]);
      setSentCode(ref.id);
      setF({ name: "", idNo: "", contact: "", section: "", text: "", website: "" });
    } catch (e) { alert("❌ Could not send: " + e.message); }
    finally { setSending(false); }
  };

  const check = async () => {
    if (!code.trim()) return;
    setChecking(true); setFound(null);
    try {
      const s = await getDoc(doc(db, "messages", code.trim()));
      setFound(s.exists() ? { id: s.id, ...s.data() } : "none");
    } catch { setFound("none"); }
    finally { setChecking(false); }
  };

  const inp = { width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #c5cae9", fontSize: "0.92rem", marginBottom: 10, fontFamily: "inherit" };
  const Thread = ({ m }) => (
    <div style={{ border: "1px solid #e0e0e0", borderRadius: 10, padding: 12, marginBottom: 10, background: "#fafbff" }}>
      <div style={{ fontSize: "0.78rem", color: "#607d8b" }}>{when(m.createdAt)} · {m.idNo}</div>
      <div style={{ margin: "6px 0", color: "#263238", whiteSpace: "pre-wrap" }}>{m.text}</div>
      {m.reply ? (
        <div style={{ background: "#e8f5e9", borderLeft: "4px solid #2e7d32", borderRadius: 6, padding: "8px 10px", color: "#1b5e20", whiteSpace: "pre-wrap" }}>
          <strong>Prof. Devaraju:</strong> {m.reply}
          <div style={{ fontSize: "0.75rem", color: "#558b2f", marginTop: 4 }}>{when(m.repliedAt)}</div>
        </div>
      ) : <div style={{ color: "#ef6c00", fontWeight: 600, fontSize: "0.85rem" }}>⏳ Awaiting reply</div>}
    </div>
  );

  return (
    <>
      <button onClick={() => { setOpen(true); setSentCode(""); }} style={{
        position: "fixed", right: 20, bottom: 20, zIndex: 1000,
        background: "linear-gradient(135deg,#1565c0,#42a5f5)", color: "#fff", border: "none",
        borderRadius: 30, padding: "12px 20px", fontWeight: 800, fontSize: "0.95rem",
        boxShadow: "0 6px 20px rgba(0,0,0,0.35)", cursor: "pointer",
      }}>💬 Ask Prof</button>

      {open && (
        <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1001, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 480, maxHeight: "90vh", overflowY: "auto", padding: 20, color: "#1a237e" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>💬 Ask Prof. Devaraju</h3>
              <button onClick={() => setOpen(false)} style={{ background: "none", border: "none", fontSize: "1.3rem", cursor: "pointer" }}>✕</button>
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              {[["send", "✉️ Send a message"], ["check", "📬 Check reply"]].map(([id, label]) => (
                <button key={id} onClick={() => setTab(id)} style={{
                  flex: 1, padding: "8px", borderRadius: 8, fontWeight: 700, cursor: "pointer",
                  border: "1.5px solid #1a237e", background: tab === id ? "#1a237e" : "#fff", color: tab === id ? "#fff" : "#1a237e",
                }}>{label}</button>
              ))}
            </div>

            {tab === "send" && (sentCode ? (
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "2rem" }}>✅</div>
                <p style={{ fontWeight: 700 }}>Message sent!</p>
                <p style={{ fontSize: "0.9rem", color: "#37474f" }}>Your reply code (keep it — take a screenshot):</p>
                <code style={{ display: "block", background: "#e3f2fd", padding: 10, borderRadius: 8, fontSize: "1rem", wordBreak: "break-all" }}>{sentCode}</code>
                <p style={{ fontSize: "0.82rem", color: "#607d8b" }}>Open <strong>📬 Check reply</strong> later. On this device your messages are listed automatically.</p>
              </div>
            ) : (
              <>
                <input style={inp} placeholder="Your name *" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} />
                <input style={inp} placeholder="Your ID (Student ID / Employee ID) *" value={f.idNo} onChange={e => setF({ ...f, idNo: e.target.value })} />
                <input style={inp} placeholder="Email or phone (for reply) *" value={f.contact} onChange={e => setF({ ...f, contact: e.target.value })} />
                <input style={inp} placeholder="Class / Section (optional, e.g. E1 CE-C2, ABI-104)" value={f.section} onChange={e => setF({ ...f, section: e.target.value })} />
                <textarea style={{ ...inp, minHeight: 110 }} maxLength={1500} placeholder="Your doubt or message *" value={f.text} onChange={e => setF({ ...f, text: e.target.value })} />
                <input tabIndex={-1} autoComplete="off" value={f.website} onChange={e => setF({ ...f, website: e.target.value })} style={{ position: "absolute", left: -9999, width: 1, height: 1, opacity: 0 }} aria-hidden="true" />
                <button onClick={send} disabled={sending} style={{ width: "100%", padding: 12, borderRadius: 8, border: "none", background: "#1a237e", color: "#fff", fontWeight: 800, cursor: "pointer" }}>
                  {sending ? "Sending…" : "Send message"}
                </button>
              </>
            ))}

            {tab === "check" && (
              <>
                <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                  <input style={{ ...inp, marginBottom: 0 }} placeholder="Paste your reply code" value={code} onChange={e => setCode(e.target.value)} />
                  <button onClick={check} disabled={checking} style={{ padding: "0 16px", borderRadius: 8, border: "none", background: "#1a237e", color: "#fff", fontWeight: 700, cursor: "pointer" }}>{checking ? "…" : "Check"}</button>
                </div>
                {found === "none" && <p style={{ color: "#c62828" }}>No message found for that code.</p>}
                {found && found !== "none" && <Thread m={found} />}
                {mine.length > 0 && (
                  <>
                    <h4 style={{ margin: "14px 0 8px" }}>Your messages on this device</h4>
                    {mine.map(m => <Thread key={m.id} m={m} />)}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/* ───────────── Admin inbox ───────────── */
export function MessagesInbox() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("new");
  const [drafts, setDrafts] = useState({});
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, "messages"), orderBy("createdAt", "desc")));
      setItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { alert("❌ " + e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveReply = async (m) => {
    const reply = (drafts[m.id] ?? m.reply ?? "").trim();
    if (!reply) { alert("Write a reply first."); return; }
    await updateDoc(doc(db, "messages", m.id), { reply, status: "replied", repliedAt: serverTimestamp() });
    load();
  };
  const setStatus = async (m, status) => { await updateDoc(doc(db, "messages", m.id), { status }); load(); };
  const remove = async (m) => { if (window.confirm("Delete this message?")) { await deleteDoc(doc(db, "messages", m.id)); load(); } };

  const counts = { new: items.filter(m => m.status === "new").length, replied: items.filter(m => m.status === "replied").length, closed: items.filter(m => m.status === "closed").length };
  const shown = items
    .filter(m => filter === "all" || m.status === filter)
    .filter(m => !search || `${m.name} ${m.idNo} ${m.contact} ${m.section} ${m.text}`.toLowerCase().includes(search.toLowerCase()));

  const btn = (bg, color = "#fff") => ({ background: bg, color, border: bg === "#fff" ? "1.5px solid #c5cae9" : "none", borderRadius: 7, padding: "7px 12px", fontWeight: 700, cursor: "pointer", fontSize: "0.82rem", textDecoration: "none" });

  return (
    <div style={{ background: "#fff", borderRadius: 12, padding: 18, color: "#1a237e" }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
        <h3 style={{ margin: "0 12px 0 0" }}>💬 Messages</h3>
        {[["new", `🆕 New (${counts.new})`], ["replied", `✅ Replied (${counts.replied})`], ["closed", `📁 Closed (${counts.closed})`], ["all", `All (${items.length})`]].map(([id, label]) => (
          <button key={id} onClick={() => setFilter(id)} style={filter === id ? btn("#1a237e") : btn("#fff", "#1a237e")}>{label}</button>
        ))}
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name / ID / text" style={{ padding: "7px 10px", borderRadius: 7, border: "1.5px solid #c5cae9", minWidth: 200 }} />
        <button onClick={load} style={btn("#fff", "#1a237e")}>🔄 Refresh</button>
      </div>

      {loading ? <p>⏳ Loading…</p> : shown.length === 0 ? <p style={{ color: "#546e7a" }}>No messages here.</p> : shown.map(m => {
        const isEmail = /@/.test(m.contact || "");
        const digits = (m.contact || "").replace(/\D/g, "");
        const phone = digits.length === 10 ? "91" + digits : digits;
        const replyText = drafts[m.id] ?? m.reply ?? "";
        return (
          <div key={m.id} style={{ border: "1.5px solid #d6dcf0", borderLeft: `5px solid ${m.status === "new" ? "#ef6c00" : m.status === "replied" ? "#2e7d32" : "#90a4ae"}`, borderRadius: 10, padding: 14, marginBottom: 12, background: "#fafbff" }}>
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 6 }}>
              <div><strong>{m.name}</strong> · <code>{m.idNo}</code>{m.section ? ` · ${m.section}` : ""}</div>
              <div style={{ fontSize: "0.8rem", color: "#607d8b" }}>{when(m.createdAt)}</div>
            </div>
            <div style={{ fontSize: "0.85rem", color: "#37474f", margin: "2px 0 8px" }}>📇 {m.contact}</div>
            <div style={{ whiteSpace: "pre-wrap", color: "#263238", marginBottom: 10 }}>{m.text}</div>
            <textarea value={replyText} onChange={e => setDrafts(d => ({ ...d, [m.id]: e.target.value }))} placeholder="Type your reply (the sender sees it with their reply code)"
              style={{ width: "100%", boxSizing: "border-box", minHeight: 70, padding: 10, borderRadius: 8, border: "1.5px solid #c5cae9", fontFamily: "inherit", marginBottom: 8 }} />
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <button onClick={() => saveReply(m)} style={btn("#2e7d32")}>💾 Save reply</button>
              {isEmail && <a href={`mailto:${m.contact}?subject=${encodeURIComponent("Re: your message to Prof. Devaraju")}&body=${encodeURIComponent(replyText)}`} style={btn("#1565c0")}>✉️ Email</a>}
              {phone.length >= 11 && <a href={`https://wa.me/${phone}?text=${encodeURIComponent(replyText)}`} target="_blank" rel="noreferrer" style={btn("#25d366")}>🟢 WhatsApp</a>}
              {m.status !== "closed" ? <button onClick={() => setStatus(m, "closed")} style={btn("#fff", "#455a64")}>📁 Close</button>
                : <button onClick={() => setStatus(m, m.reply ? "replied" : "new")} style={btn("#fff", "#455a64")}>↩ Reopen</button>}
              <button onClick={() => remove(m)} style={btn("#fff", "#c62828")}>🗑 Delete</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
